import {
  EuiFormRow,
  EuiIcon,
  EuiInputPopover,
  EuiSelectable,
  EuiSelectableOption,
} from "@inscopix/ideas-eui";
import { useDataTableContext } from "pages/gdt/store/DataTableProvider";
import { DataTableStoreState } from "pages/gdt/store/DataTableProvider.types";
import { useState } from "react";

interface FieldTableProps {
  autoFocus?: boolean;
  disabled?: boolean;
  label?: string;
  filter?: {
    includeTable?: (table: DataTableStoreState["tables"][number]) => boolean;
    disabledTable?: (table: DataTableStoreState["tables"][number]) => boolean;
  };
  value: string | undefined;
  onChange: (
    value:
      | {
          id: string;
          key: string;
          name: string;
          kind: "data_table" | "gdt_analysis_table";
        }
      | undefined,
  ) => void;
}

export const FieldTable = ({
  autoFocus = false,
  disabled = false,
  filter,
  label = "Table",
  value,
  onChange,
}: FieldTableProps) => {
  const tables = useDataTableContext((s) => s.tables);
  const [isOpen, setIsOpen] = useState(autoFocus);
  const [searchValue, setSearchValue] = useState(() => {
    const table = tables.find(({ id }) => id === value);
    return table ? `${table.name} (${table.key})` : "";
  });

  const options: EuiSelectableOption[] = tables
    // Apply filters
    .filter((table) => {
      return filter?.includeTable ? filter.includeTable(table) : true;
    })
    .map((table) => ({
      key: table.id,
      checked: table.id === value ? "on" : undefined,
      label: `${table.name} (${table.key})`,
      disabled: filter?.disabledTable ? filter.disabledTable(table) : false,
      prepend: (
        <EuiIcon
          type={
            table.kind === "analysis" ? "indexMapping" : "tableDensityNormal"
          }
        />
      ),
    }));

  const handleChange = (tableId: string) => {
    const table = tables.find((table) => table.id === tableId);
    if (table === undefined || table?.id === value) {
      onChange(undefined);
    } else {
      onChange({
        id: table.id,
        key: table.key,
        name: table.name,
        kind: table.kind === "data" ? "data_table" : "gdt_analysis_table",
      });
      setSearchValue(`${table.name} (${table.key})`);
    }
    setIsOpen(false);
  };

  return (
    <EuiFormRow fullWidth label={label}>
      <EuiSelectable
        aria-label="Select a table"
        searchable
        singleSelection
        options={options}
        onChange={(options) => {
          const checkedOption = options.find((o) => o.checked === "on");
          if (checkedOption?.key !== undefined) {
            handleChange(checkedOption.key);
          }
        }}
        listProps={{
          isVirtualized: true,
          rowHeight: 40,
          showIcons: false,
        }}
        searchProps={{
          disabled,
          placeholder: "Select a table...",
          value: searchValue,
          onChange: (value) => setSearchValue(value),
          onFocus: () => setIsOpen(true),
        }}
      >
        {(list, search) => (
          <EuiInputPopover
            closePopover={() => setIsOpen(false)}
            disableFocusTrap
            closeOnScroll
            isOpen={isOpen}
            input={search ?? ""}
            panelPaddingSize="none"
            fullWidth
          >
            {list}
          </EuiInputPopover>
        )}
      </EuiSelectable>
    </EuiFormRow>
  );
};
